// src/services/soRulesEngine.ts
// ----------------------------
// PURE Sales Order rules engine (docs/so-rules-engine.md). Turns a request's
// approved items into one or more Unleashed Sales Orders by applying the ordered
// `so_rules` pipeline: each rule claims a slice of the pool, groups it into SO(s)
// by its grouping key, and the items are removed from the pool. Rules that emit
// the same (suffix, group-key) MERGE into one SO. Anything left after every rule
// is swept into a hard-coded `ZZZ` safety-net SO (the caller also alerts).
//
// PURE: no DB / network / Date. The caller injects the product-code resolvers and
// the existing-suffix counts (for numbering), so this is fully unit-testable
// against the §8 worked examples.

import { APPROVAL_STATUSES } from "../utils/enums";
import { calcUniItems } from "../utils/formConstants";

const APPROVED = APPROVAL_STATUSES.APPROVED;
const ZZZ = "ZZZ";

// ── Types ────────────────────────────────────────────────────────────────────
export type ItemKind = "device" | "shoe" | "fee" | "uniform";

// The subset of a so_rules row the engine reads.
export interface SoRule {
  // Row id — used only as the tiebreak when two rules share a priority, so the
  // evaluation order is deterministic. Optional: test fixtures omit it.
  id?: number | string | null;
  priority: number;
  label?: string;
  match_kind: ItemKind;
  match_fee_types: string[] | null;
  match_state: string | null;
  match_supplier_kind: string | null;
  match_supplier_suffix: string | null;
  match_payment_method: string | null;
  suffix: string | null; // null = use the supplier's so_suffix
  group_by: string;
  line_mode: string; // per_child | aggregate
  payee_type: string | null;
  // Per-rule Customer Reference template (db-78), same {{tokens}} as renderSoTemplate.
  // null = fall back to the global so_customer_reference_template.
  customer_reference?: string | null;
  // Per-rule per-CHILD line-comment template (db-106). Tokens {{child_code}} {{description}}.
  // null/blank = default "{{child_code}}" (the pre-db-106 behaviour). Used only on
  // per_child line mode; aggregate lines ignore it.
  line_comment_template?: string | null;
  // db-114: this rule's SOs must be processed by a picker before they push — the queue
  // writes them with status='hold' (db-113) until the picker releases them.
  requires_picker?: boolean;
  active?: boolean;
}

export interface SupplierMeta {
  so_suffix?: string | null;
  kind?: string | null;
  payment_method?: string | null;
}

export interface EngineItem {
  kind: ItemKind;
  feeType?: string;
  deviceType?: string;
  shoeKind?: "jogger" | "leather";
  supplier?: string;
  supplierMeta?: SupplierMeta | null;
  childIndex: number; // 0-based
  childCode: string; // e.g. "MY2"
  yearLevel?: string | number | null; // raw year level ("Year 3" / "Prep / Kindy"), for the stationery year bucket
  school?: string | null;
  provider?: string | null;
  qty: number;
  price: number;
  // children_devices.id / children_fees.id for this line (device + fee only) — used
  // to stamp the nominated SO number back onto the record at Confirm & Send (db-71).
  id?: string | null;
}

export interface OrderLine {
  line_number: number;
  product_code: string;
  order_quantity: number;
  unit_price: number;
  line_comments: string | null;
}

export interface UnmappedRef {
  kind: ItemKind;
  key: string;
  childCode: string;
}

export interface BuiltSO {
  suffix: string;
  sequence: number; // 1 = the first of this suffix (unnumbered in the ref)
  order_number: string; // <ref>-<suffix>[seq]
  payee_type: string | null;
  payee_identifier: string | null;
  childCodes: string[]; // unique child refs on this SO, in child order (e.g. ["MY2","NY9"])
  schools: string[]; // unique non-empty school names on this SO, in child order
  // The Customer Reference template for THIS SO — the customer_reference of the
  // HIGHEST-priority (lowest priority number) rule that contributed items (db-78).
  // null = that rule left it blank → the writer falls back to the global template.
  customerReferenceTemplate: string | null;
  // db-114: this SO must be picker-processed before it pushes (queued as 'hold', db-113).
  requiresPicker: boolean;
  lines: OrderLine[];
  unmapped: UnmappedRef[];
  items: EngineItem[]; // the source items this SO claimed (for the db-71 so_number write-back)
}

export interface EngineResult {
  orders: BuiltSO[]; // includes the ZZZ SO (last) if any items were unrouted
  zzzUnrouted: EngineItem[]; // the items that fell to the safety net (for the alert)
  // Money reconciliation — every approved dollar must reach an SO line. `expectedTotal`
  // is the sum of all approved-this-session item amounts; `builtTotal` is the sum of all
  // SO line totals (the $0 DISCOUNT lines don't count). They differ ONLY when an item was
  // dropped — e.g. it matched a rule but had no product code (see `unmapped`). The caller
  // MUST alert on any mismatch (docs/so-rules-engine.md §3.5).
  expectedTotal: number;
  builtTotal: number;
  // Items that matched a rule (so were routed to a real SO) but could NOT be lined
  // because their product code was missing — the usual cause of a money shortfall.
  // (Distinct from `zzzUnrouted`, which is items NO rule matched.)
  unmapped: UnmappedRef[];
}

export interface EngineLookups {
  // yearBucket (db-115) refines stationery by school year (STAP/STA1/…); the resolver
  // tries (kind, key, yearBucket) then falls back to the base (kind, key, "") row.
  productCode: (
    kind: "device" | "fee" | "shoe",
    key: string,
    yearBucket?: string
  ) => string | null | undefined;
  uniformProductCode: (supplier: string) => string | null | undefined;
  // How many SOs of a suffix already exist for this request (prior sessions), so
  // the first this session is numbered correctly. Defaults to 0.
  existingSuffixCount?: (suffix: string) => number;
  discountCode?: string; // default "DISCOUNT"
}

export interface RequestForEngine {
  refNo?: string | null;
  state?: string | null;
  children?: unknown[] | null;
}

export interface BuildOptions {
  confirmedAt?: string; // include only items confirmed this session
  supplierMeta?: (supplier: string) => SupplierMeta | null | undefined;
}

// ── Helpers ──────────────────────────────────────────────────────────────────
const num = (v: unknown): number => {
  const n = Number.parseFloat(String(v ?? ""));
  return Number.isFinite(n) ? n : 0;
};

// "MY2" — gender initial + Y + year token (P for prep). Layout is refinable
// (docs/so-rules-engine.md §9.5); the child ref is what appears in per-child line
// comments and the SO comments block.
export function childCode(child: {
  gender?: string | null;
  yearLevel?: string | number | null;
}): string {
  const g =
    String(child.gender ?? "")
      .trim()
      .charAt(0)
      .toUpperCase() || "?";
  const yRaw = String(child.yearLevel ?? "").trim();
  const y = /^p/i.test(yRaw) ? "P" : yRaw.replace(/[^0-9]/g, "") || yRaw;
  return `${g}Y${y}`;
}

// Map a child's year level to a stationery year bucket (db-115 / db-116 seed keys):
//   Prep / Kindy → "PREP", Year 1 → "Y1", Year 2 → "Y2", Years 3-4 → "Y3_4",
//   Years 5-6 → "Y5_6", Years 7-12 → "HS". Anything else (TAFE, blank, unknown)
//   → "" so the product-code lookup falls back to the base stationery row.
export function yearBucket(yearLevel?: string | number | null): string {
  const raw = String(yearLevel ?? "").trim();
  if (!raw) return "";
  if (/prep|kindy/i.test(raw) || /^p/i.test(raw)) return "PREP";
  const n = Number.parseInt(raw.replace(/[^0-9]/g, ""), 10);
  if (!Number.isFinite(n) || n <= 0) return "";
  if (n === 1) return "Y1";
  if (n === 2) return "Y2";
  if (n === 3 || n === 4) return "Y3_4";
  if (n === 5 || n === 6) return "Y5_6";
  if (n >= 7 && n <= 12) return "HS";
  return "";
}

// Build the pool of orderable items from a request's approved-this-session items.
export function itemsFromRequest(request: RequestForEngine, opts: BuildOptions = {}): EngineItem[] {
  const { confirmedAt, supplierMeta } = opts;
  const inc = (
    item: { approvalStatus?: string; confirmedAt?: string | null } | null | undefined
  ): boolean =>
    !!item &&
    item.approvalStatus === APPROVED &&
    (!confirmedAt || item.confirmedAt === confirmedAt);

  const out: EngineItem[] = [];
  (request.children ?? []).forEach((c, i) => {
    // biome-ignore lint/suspicious/noExplicitAny: child is a loose app object (see src/types/database.ts ChildRow)
    const child = c as any;
    const cc = childCode(child);
    // The real field is `schoolName` (get_request_children maps request_children.school_name
    // → "schoolName"); `school` is only ever present in test fixtures. Read schoolName first so
    // {{school_names}} / per_school grouping / the school payee work on live data.
    const base = {
      childIndex: i,
      childCode: cc,
      yearLevel: child.yearLevel ?? null,
      school: child.schoolName ?? child.school ?? null,
    };

    if (inc(child.laptop)) {
      out.push({
        ...base,
        kind: "device",
        deviceType: child.laptop.deviceType,
        qty: 1,
        price: num(child.laptop.estimatedCost),
        id: child.laptop.id ?? null,
      });
    }
    for (const f of child.fees ?? []) {
      if (!inc(f)) continue;
      out.push({
        ...base,
        kind: "fee",
        feeType: f.feeType,
        provider: f.feeDesc ?? null,
        qty: 1,
        price: num(f.feeAmount),
        id: f.id ?? null,
      });
    }
    if (inc(child.shoes)) {
      const s = child.shoes;
      if (num(s.jQty) > 0)
        out.push({
          ...base,
          kind: "shoe",
          shoeKind: "jogger",
          qty: num(s.jQty),
          price: num(s.jCost),
        });
      if (num(s.lQty) > 0)
        out.push({
          ...base,
          kind: "shoe",
          shoeKind: "leather",
          qty: num(s.lQty),
          price: num(s.lCost),
        });
    }
    if (inc(child.uniforms)) {
      const supplier = child.uniforms.supplier || "";
      out.push({
        ...base,
        kind: "uniform",
        supplier,
        supplierMeta: supplierMeta ? (supplierMeta(supplier) ?? null) : null,
        qty: 1,
        price: calcUniItems([child.uniforms as never]),
      });
    }
  });
  return out;
}

function itemMatchesRule(item: EngineItem, rule: SoRule, requestState: string): boolean {
  if (item.kind !== rule.match_kind) return false;
  if (rule.match_state && requestState !== rule.match_state) return false;
  if (rule.match_fee_types && rule.match_fee_types.length > 0) {
    if (!rule.match_fee_types.includes(item.feeType ?? "")) return false;
  }
  const meta = item.supplierMeta ?? {};
  if (rule.match_supplier_kind && meta.kind !== rule.match_supplier_kind) return false;
  if (rule.match_supplier_suffix && meta.so_suffix !== rule.match_supplier_suffix) return false;
  if (rule.match_payment_method && meta.payment_method !== rule.match_payment_method) return false;
  return true;
}

function groupKeyFor(item: EngineItem, groupBy: string): string {
  switch (groupBy) {
    case "per_child":
      return `child:${item.childIndex}`;
    case "per_school":
      return `school:${item.school ?? ""}`;
    case "per_provider":
      return `provider:${item.provider ?? ""}`;
    case "per_supplier":
      return `supplier:${item.supplier ?? ""}`;
    default:
      return "request";
  }
}

function payeeIdentifierFor(item: EngineItem, payeeType: string | null): string | null {
  switch (payeeType) {
    case "school":
      return item.school ?? null;
    case "provider":
      return item.provider ?? null;
    case "supplier":
      return item.supplier ?? null;
    default:
      return null;
  }
}

const keyOf = (item: EngineItem): string =>
  item.kind === "device"
    ? (item.deviceType ?? "")
    : item.kind === "fee"
      ? (item.feeType ?? "")
      : (item.shoeKind ?? "");

const resolveCode = (item: EngineItem, lk: EngineLookups): string | null | undefined =>
  item.kind === "uniform"
    ? lk.uniformProductCode(item.supplier ?? "")
    : lk.productCode(item.kind, keyOf(item), yearBucket(item.yearLevel));

// A staged SO group (before lines/numbering). taggedItems carry the line_mode of
// the rule that claimed them (a merged SO can mix per_child + aggregate items).
interface Group {
  suffix: string;
  payeeType: string | null;
  payeeId: string | null;
  // True if ANY rule that fed this SO requires a picker (db-114) → queued as 'hold'.
  requiresPicker: boolean;
  // The Customer Reference template + the priority of the rule that set it. On a
  // merged group the HIGHEST-priority (lowest priority number) contributing rule wins.
  refTemplate: string | null;
  refPriority: number;
  // lineCommentTemplate = the CLAIMING rule's per-child line-comment template
  // (db-106); each item carries its own so a merged SO renders each line correctly.
  items: { item: EngineItem; lineMode: string; lineCommentTemplate: string | null }[];
}

// db-106: render a per-CHILD SO line comment from a rule's template. Tokens:
//   {{child_code}}   the line's child ref (e.g. MY2)
//   {{description}}  the fee's description (item.provider); blank on non-fee lines
//                    (device / shoe / uniform have no fee description).
// A null/blank template falls back to "{{child_code}}" — the pre-db-106 behaviour,
// so untouched rules keep emitting just the child code. Output is trimmed and hard-
// capped at LINE_COMMENT_MAX chars for Unleashed's SalesOrderLine.Comments field.
// The true max IS documented: the SalesOrderLine.Comments field is String(1024)
// (Unleashed API SalesOrders schema, confirmed 29 Jul 2026).
const LINE_COMMENT_MAX = 1024;
export function renderLineComment(template: string | null | undefined, item: EngineItem): string {
  const tpl = template?.trim() ? template : "{{child_code}}";
  const values: Record<string, string> = {
    child_code: item.childCode ?? "",
    description: item.provider ?? "",
  };
  const rendered = tpl
    .replace(/\{\{\s*([a-z_]+)\s*\}\}/g, (m, key) => (key in values ? values[key] : m))
    .trim();
  return rendered.length > LINE_COMMENT_MAX ? rendered.slice(0, LINE_COMMENT_MAX) : rendered;
}

function buildLines(
  group: Group,
  lk: EngineLookups
): { lines: OrderLine[]; unmapped: UnmappedRef[] } {
  const lines: OrderLine[] = [];
  const unmapped: UnmappedRef[] = [];
  let n = 0;

  // per_child items → one line each (child ref in comments), by child order.
  const perChild = group.items
    .filter((t) => t.lineMode !== "aggregate")
    .sort((a, b) => a.item.childIndex - b.item.childIndex);
  for (const entry of perChild) {
    const { item } = entry;
    const code = resolveCode(item, lk);
    if (!code) {
      unmapped.push({
        kind: item.kind,
        key: keyOf(item) || item.supplier || "",
        childCode: item.childCode,
      });
      continue;
    }
    n += 1;
    lines.push({
      line_number: n,
      product_code: code,
      order_quantity: item.qty,
      unit_price: item.price,
      line_comments: renderLineComment(entry.lineCommentTemplate, item),
    });
  }

  // aggregate items → one line per product code; qty summed; weighted unit price
  // so line_total = Σ(qty×price).
  const agg = group.items
    .filter((t) => t.lineMode === "aggregate")
    .sort((a, b) => a.item.childIndex - b.item.childIndex);
  const byCode = new Map<string, { qty: number; total: number; children: string[] }>();
  const order: string[] = [];
  for (const { item } of agg) {
    const code = resolveCode(item, lk);
    if (!code) {
      unmapped.push({ kind: item.kind, key: keyOf(item) || "", childCode: item.childCode });
      continue;
    }
    if (!byCode.has(code)) {
      byCode.set(code, { qty: 0, total: 0, children: [] });
      order.push(code);
    }
    const e = byCode.get(code) as { qty: number; total: number; children: string[] };
    e.qty += item.qty;
    e.total += item.qty * item.price;
    // Name the distinct children feeding this merged line, in child order (e.g. "FY4, MY7").
    if (item.childCode && !e.children.includes(item.childCode)) e.children.push(item.childCode);
  }
  for (const code of order) {
    const e = byCode.get(code) as { qty: number; total: number; children: string[] };
    n += 1;
    lines.push({
      line_number: n,
      product_code: code,
      order_quantity: e.qty,
      unit_price: e.qty ? e.total / e.qty : 0,
      line_comments: e.children.length ? e.children.join(", ") : null,
    });
  }

  // Final Discount line ($0) — a hard-coded API contract.
  n += 1;
  lines.push({
    line_number: n,
    product_code: lk.discountCode || "DISCOUNT",
    order_quantity: 1,
    unit_price: 0,
    line_comments: null,
  });

  return { lines, unmapped };
}

// Main entry: request → the Sales Orders.
export function buildSalesOrders(
  request: RequestForEngine,
  rules: SoRule[],
  lookups: EngineLookups,
  opts: BuildOptions = {}
): EngineResult {
  const ref = String(request.refNo ?? "");
  const state = String(request.state ?? "");
  let pool = itemsFromRequest(request, opts);
  // Capture the expected total up front (before the pool is consumed) — every
  // approved dollar must end up on an SO line.
  const expectedTotal = pool.reduce((s, it) => s + it.qty * it.price, 0);

  // Ordered, consuming pass → groups keyed by (suffix|groupKey).
  const groups = new Map<string, Group>();
  const groupOrder: string[] = [];
  // Tiebreak on id. Array.sort is stable, so two rules sharing a priority used to
  // keep whatever order the DB read happened to return — and Postgres guarantees no
  // order for tied rows, so the same request could route to a different suffix,
  // grouping or payee between runs. Sorting by id second makes it deterministic and
  // matches the `.order("priority").order("id")` in data/soRules.ts.
  // The ambiguity itself is surfaced to admins by services/soRuleConflicts.ts.
  const activeRules = rules
    .filter((r) => r.active !== false)
    .sort((a, b) => a.priority - b.priority || Number(a.id ?? 0) - Number(b.id ?? 0));

  const claim = (item: EngineItem, suffix: string, gkey: string, rule: SoRule) => {
    const mapKey = `${suffix}|${gkey}`;
    let g = groups.get(mapKey);
    if (!g) {
      g = {
        suffix,
        payeeType: rule.payee_type,
        payeeId: payeeIdentifierFor(item, rule.payee_type),
        refTemplate: rule.customer_reference ?? null,
        refPriority: rule.priority,
        requiresPicker: !!rule.requires_picker,
        items: [],
      };
      groups.set(mapKey, g);
      groupOrder.push(mapKey);
    } else if (rule.priority < g.refPriority) {
      // A higher-priority (lower number) rule also feeds this merged SO — it sets the reference.
      g.refTemplate = rule.customer_reference ?? null;
      g.refPriority = rule.priority;
    }
    // Any contributing rule that needs a picker marks the whole SO (db-114).
    g.requiresPicker = g.requiresPicker || !!rule.requires_picker;
    g.items.push({
      item,
      lineMode: rule.line_mode,
      lineCommentTemplate: rule.line_comment_template ?? null,
    });
  };

  for (const rule of activeRules) {
    const remaining: EngineItem[] = [];
    for (const item of pool) {
      if (!itemMatchesRule(item, rule, state)) {
        remaining.push(item);
        continue;
      }
      const suffix = rule.suffix ?? item.supplierMeta?.so_suffix ?? ZZZ;
      claim(item, suffix, groupKeyFor(item, rule.group_by), rule);
    }
    pool = remaining;
  }

  // Safety net: sweep anything unrouted into a single <ref>-ZZZ SO (per_request,
  // per_child lines).
  const zzzUnrouted = pool.slice();
  if (pool.length > 0) {
    const mapKey = `${ZZZ}|request`;
    const g: Group = {
      suffix: ZZZ,
      payeeType: "dfv_service",
      payeeId: null,
      refTemplate: null, // ZZZ falls back to the global customer-reference template
      refPriority: Number.MAX_SAFE_INTEGER,
      requiresPicker: false, // the safety-net SO is never auto-held
      items: pool.map((item) => ({ item, lineMode: "per_child", lineCommentTemplate: null })),
    };
    groups.set(mapKey, g);
    groupOrder.push(mapKey);
  }

  // Build each SO, numbering per suffix (existing prior-session count + build order).
  const seqSeen = new Map<string, number>();
  const existing = lookups.existingSuffixCount ?? (() => 0);
  const orders: BuiltSO[] = [];
  for (const mapKey of groupOrder) {
    const g = groups.get(mapKey) as Group;
    const prior = seqSeen.get(g.suffix) ?? existing(g.suffix);
    const sequence = prior + 1;
    seqSeen.set(g.suffix, sequence);
    const orderNumber = sequence > 1 ? `${ref}-${g.suffix}${sequence}` : `${ref}-${g.suffix}`;
    const { lines, unmapped } = buildLines(g, lookups);
    // Unique child refs + schools on this SO, in child order — for the comments
    // block + customer-reference templates (renderSoTemplate).
    const sorted = [...g.items].sort((a, b) => a.item.childIndex - b.item.childIndex);
    const childCodes: string[] = [];
    const schools: string[] = [];
    for (const { item } of sorted) {
      if (item.childCode && !childCodes.includes(item.childCode)) childCodes.push(item.childCode);
      const sch = (item.school ?? "").trim();
      if (sch && !schools.includes(sch)) schools.push(sch);
    }
    orders.push({
      suffix: g.suffix,
      sequence,
      order_number: orderNumber,
      payee_type: g.payeeType,
      payee_identifier: g.payeeId,
      childCodes,
      schools,
      customerReferenceTemplate: g.refTemplate,
      requiresPicker: g.requiresPicker,
      lines,
      unmapped,
      items: g.items.map((t) => t.item),
    });
  }

  // Money reconciliation + the routed-but-unmapped items (across all orders).
  const builtTotal = orders.reduce(
    (s, o) => s + o.lines.reduce((ls, l) => ls + l.order_quantity * l.unit_price, 0),
    0
  );
  const unmapped = orders.flatMap((o) => o.unmapped);

  return { orders, zzzUnrouted, expectedTotal, builtTotal, unmapped };
}

// ── SO comments / customer-reference templates (docs/so-rules-engine.md §2) ────
// Editable at Manage → Unleashed → SO Rules (portal_config keys
// `so_comments_template` / `so_customer_reference_template`). Rendered per SO at
// Confirm & Send; the comments block goes in request_sales_orders.comments and the
// customer reference in .customer_reference.

// The tokens both templates understand. Per-SO fields (childCodes/schools) are
// scoped to the items on THAT SO; the caseworker/delivery/service fields are
// request-level.
export interface SoTemplateContext {
  childCodes: string[];
  schools: string[];
  caseworkerName?: string | null;
  caseworkerEmail?: string | null;
  caseworkerMobile?: string | null;
  delivery?: string | null;
  dfvService?: string | null;
  refNo?: string | null;
  suffix?: string | null;
}

export const DEFAULT_SO_COMMENTS_TEMPLATE =
  "Children: {{child_codes}}\n" +
  "Caseworker: {{caseworker_name}} — {{caseworker_email}} / {{caseworker_mobile}}\n" +
  "DFV Service: {{dfv_service}}\n" +
  "Deliver to: {{delivery}}";

export const DEFAULT_SO_CUSTOMER_REFERENCE = "{{school_names}}";

// Substitute the {{tokens}} in a template with the context values. Unknown tokens
// are left as-is; a missing/empty value renders as an empty string. Any leftover
// blank lines (from an all-empty line) are trimmed so the block stays tidy.
export function renderSoTemplate(template: string, ctx: SoTemplateContext): string {
  const values: Record<string, string> = {
    child_codes: (ctx.childCodes ?? []).join(" "),
    school_names: (ctx.schools ?? []).join(", "),
    caseworker_name: ctx.caseworkerName ?? "",
    caseworker_email: ctx.caseworkerEmail ?? "",
    caseworker_mobile: ctx.caseworkerMobile ?? "",
    delivery: ctx.delivery ?? "",
    dfv_service: ctx.dfvService ?? "",
    ref_no: ctx.refNo ?? "",
    suffix: ctx.suffix ?? "",
  };
  const rendered = String(template ?? "").replace(/\{\{\s*([a-z_]+)\s*\}\}/g, (m, key) =>
    key in values ? values[key] : m
  );
  // Drop lines that ended up completely blank (e.g. a "Deliver to: " with no value
  // would still keep its label; but a line that was only tokens collapses to "").
  return rendered
    .split("\n")
    .filter((line) => line.trim() !== "")
    .join("\n")
    .trim();
}
